import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';
import { Slot } from 'radix-ui';
import { cn } from '@/lib/utils';

/*
 * shadcn/ui Button tuned to design/DESIGN.md: 8 px radius, the brand orange only on `default`, 44 px targets on
 * phones, 150 ms ease-out. `loading` (spinner + aria-busy + disabled) and `type="button"` by default are the only
 * additions over the registry.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-card hover:bg-primary-hover active:translate-y-px',
        destructive: 'border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:ring-destructive/30',
        outline: 'border border-border bg-secondary text-foreground hover:border-border-strong hover:bg-surface-3',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-surface-3',
        ghost: 'text-muted-foreground hover:bg-accent hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-3.5 max-sm:h-11',
        sm: 'h-8 gap-1.5 px-2.5 text-[13px] max-sm:h-11',
        lg: 'h-11 px-5 text-[15px]',
        icon: 'size-9 max-sm:size-11',
        'icon-sm': 'size-8 max-sm:size-11',
        'icon-lg': 'size-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Shows a spinner, marks the control busy and disables it while a request is in flight. */
    loading?: boolean;
  };

/** `type="button"` unless told otherwise (not on a Slot child), and `loading` marks the control busy and disabled. */
function buttonState(o: { asChild: boolean; type: ButtonProps['type']; disabled: boolean | undefined; loading: boolean }) {
  return {
    type: o.asChild ? o.type : (o.type ?? 'button'),
    disabled: o.disabled || o.loading || undefined,
    'aria-busy': o.loading || undefined,
  };
}

function buttonContent(asChild: boolean, loading: boolean, children: React.ReactNode) {
  // Slot needs exactly one child: the spinner slot must not exist here (even as null it counts).
  if (asChild) return children;
  return (
    <>
      {loading ? <Loader2Icon className="animate-spin motion-reduce:animate-none" aria-hidden /> : null}
      {children}
    </>
  );
}

function Button({ className, variant = 'default', size = 'default', asChild = false, loading = false, disabled, type, children, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      {...buttonState({ asChild, type, disabled, loading })}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {buttonContent(asChild, loading, children)}
    </Comp>
  );
}

export { Button, buttonVariants };
